// src/modules/clinical-note-ai/pages/ClinicalNoteAiPage.jsx

import React, { useMemo, useState } from 'react';
import {
  Container,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Button,
  Stack,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PsychologyIcon from '@mui/icons-material/Psychology';

import { analyzeClinicalPdfWithAI } from '../services/clinicalNoteAiService';
import { DEFAULT_ANALYSIS } from '../constants/analysisSchema';
import PdfUploadCard from '../components/PdfUploadCard';
import AnalysisResultCard from '../components/AnalysisResultCard';
import AnalysisSkeleton from '../components/AnalysisSkeleton';

const ClinicalNoteAiPage = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(DEFAULT_ANALYSIS);
  const [error, setError] = useState('');

  const canAnalyze = useMemo(() => !!file && !loading, [file, loading]);

  const handleAnalyze = async () => {
    setError('');
    setLoading(true);
    try {
      const result = await analyzeClinicalPdfWithAI({ file, model: 'gpt-4o-mini' });
      setAnalysis(result);
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Error analyzing PDF');
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setFile(null);
    setAnalysis(DEFAULT_ANALYSIS);
    setError('');
  };

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Stack spacing={2} sx={{ mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <PsychologyIcon />
          <Typography variant="h5" fontWeight={800}>
            Clinical Notes Data Analytics
          </Typography>
        </Stack>

        <Alert severity="info">
          Output is de-identified and must be reviewed by a clinician.
        </Alert>

        {error ? <Alert severity="error">{error}</Alert> : null}
      </Stack>

      <Grid container spacing={2}>
        <Grid item xs={12} md={5}>
          <Card sx={{ borderRadius: 3 }}>
            <CardHeader
              title="Upload PDF"
              subheader="Scanned PDFs are supported (image-based)"
              avatar={<PictureAsPdfIcon />}
            />
            <CardContent>
              <Stack spacing={2}>
                <PdfUploadCard file={file} onChange={setFile} />

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="contained"
                    onClick={handleAnalyze}
                    disabled={!canAnalyze}
                    fullWidth
                  >
                    Analyze
                  </Button>
                  <Button variant="outlined" onClick={handleClear} disabled={loading} fullWidth>
                    Clear
                  </Button>
                </Stack>

                <Divider />
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={7}>
          {loading ? <AnalysisSkeleton /> : <AnalysisResultCard analysis={analysis} />}
        </Grid>
      </Grid>
    </Container>
  );
};

export default ClinicalNoteAiPage;
